import androidx.compose.runtime.*
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*

/** Fixed-width home:away pair so the ":" always lands at the same spot regardless of digit count. */
@Composable
private fun MatchScore(homeText: String, awayText: String, homeHighlight: Boolean = false, awayHighlight: Boolean = false) {
    Span(attrs = { classes("match-score") }) {
        Span(attrs = { classes("score-home") }) {
            if (homeHighlight) B(attrs = { classes("duel-winner") }) { Text(homeText) } else Text(homeText)
        }
        Span(attrs = { classes("score-sep") }) { Text(":") }
        Span(attrs = { classes("score-away") }) {
            if (awayHighlight) B(attrs = { classes("duel-winner") }) { Text(awayText) } else Text(awayText)
        }
    }
}

@Composable
fun MatchDetailModal(
    matchId: String,
    homeDuelPoints: Int?,
    awayDuelPoints: Int?,
    homeTeamName: String,
    awayTeamName: String,
    leagueName: String? = null,
    onClose: () -> Unit,
) {
    var match by remember { mutableStateOf<MatchDto?>(null) }
    var duels by remember { mutableStateOf<List<MatchDuelDto>>(emptyList()) }
    var lineup by remember { mutableStateOf<List<MatchLineupDto>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(matchId) {
        loading = true
        try {
            match = Api.fetchMatch(null, matchId)
            duels = Api.fetchMatchDuels(null, matchId)
            lineup = Api.fetchMatchLineup(null, matchId)
            error = null
        } catch (e: Throwable) {
            error = "Načtení detailu selhalo: ${e.apiMessage()}"
        }
        loading = false
    }

    val playerNameByPosition = remember(lineup) { lineup.associate { it.position to (it.player?.name ?: "?") } }
    val home = homeDuelPoints ?: 0
    val away = awayDuelPoints ?: 0

    Div(attrs = { classes("modal-backdrop") }) {
        Div(attrs = { classes("match-modal") }) {
            Div(attrs = { classes("match-modal-header") }) {
                H2 { Text("$homeTeamName vs $awayTeamName") }
                Button(attrs = { onClick { onClose() } }) { Text("Zavřít") }
            }
            match?.let { m ->
                Div(attrs = { classes("match-modal-meta") }) {
                    val leaguePrefix = leagueName?.let { "$it · " } ?: ""
                    Text("$leaguePrefix${roundLabel(m.phase, m.roundNumber)} · ${formatDate(m.playedAt)}")
                    // Inline in the meta line (not its own row) -- keeps the modal's content height
                    // the same as before this feature, so .match-modal's max-height/overflow:auto
                    // doesn't start showing a scrollbar it didn't need to before.
                    m.protocolImagePath?.let { path ->
                        Span(attrs = {
                            classes("match-modal-photo-link")
                            onClick { openLightbox(Api.matchProtocolImageUrl(path)) }
                        }) { Text(" · 📷 Zápis o utkání") }
                    }
                }
                if (m.isForfeit) {
                    val (loser, winner) = if (m.result == "home_forfeit") homeTeamName to awayTeamName else awayTeamName to homeTeamName
                    Div(attrs = { classes("editor-error") }) {
                        Text("Tento zápas byl kontumován — prohrává $loser, vítězí $winner 9:0.")
                    }
                }
            }
            if (loading) Text("Načítám…")
            error?.let { Div { Text("Chyba: $it") } }
            Table(attrs = { classes("match-table") }) {
                Tr {
                    Th(attrs = { if (home > away) classes("match-team-winner") }) { Text(homeTeamName) }
                    Th { }
                    Th(attrs = { if (away > home) classes("match-team-winner") }) { Text(awayTeamName) }
                }
                duels.forEach { d ->
                    val homeLabel = playerNameByPosition[d.homePosition] ?: d.homePosition
                    val awayLabel = playerNameByPosition[d.awayPosition] ?: d.awayPosition
                    val homeVal = if (d.homePlayed) d.homeScore ?: 0 else 0
                    val awayVal = if (d.awayPlayed) d.awayScore ?: 0 else 0
                    val homeScoreText = if (d.homePlayed) homeVal.toString() else "—"
                    val awayScoreText = if (d.awayPlayed) awayVal.toString() else "—"
                    val isTie = d.homePlayed && d.awayPlayed && homeVal == awayVal
                    val homeHighlight = homeVal > awayVal || isTie
                    val awayHighlight = awayVal > homeVal || isTie
                    Tr {
                        Td { if (homeHighlight) B(attrs = { classes("duel-winner") }) { Text(homeLabel) } else Text(homeLabel) }
                        Td { MatchScore(homeScoreText, awayScoreText, homeHighlight, awayHighlight) }
                        Td { if (awayHighlight) B(attrs = { classes("duel-winner") }) { Text(awayLabel) } else Text(awayLabel) }
                    }
                }
                Tr(attrs = { classes("match-total-row") }) {
                    Td { Text("Kuželky celkem") }
                    Td { MatchScore(match?.homeTotalScore?.toString() ?: "—", match?.awayTotalScore?.toString() ?: "—") }
                    Td { }
                }
                Tr(attrs = { classes("match-points-row") }) {
                    Td(attrs = { attr("colspan", "3") }) { Text("$home : $away") }
                }
            }
        }
    }
}
